import { ScreenHelp } from "@/components/Help";
import { ErrorState, LoadingState } from "@/components/States";
import {
  Card,
  Field,
  Icon,
  Pill,
  SectionTitle,
  Select,
  TableScroll,
  TextInput,
} from "@/components/ui";
import type { Contact, Customer } from "@/lib/data";
import { useCustomers } from "@/lib/hooks";
import { useIsMobile } from "@/lib/useViewport";
import { type CSSProperties, useState } from "react";

const Head = ({ cols }: { cols: { t: string; a?: "left" | "right" | "center" }[] }) => (
  <thead style={{ background: "var(--bg-card-hover)" }}>
    <tr
      style={{
        color: "var(--text-dim)",
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: "0.4px",
      }}
    >
      {cols.map((c) => (
        <th
          key={c.t || `sp-${c.a}`}
          style={{
            textAlign: c.a || "left",
            padding: "10px 20px",
            fontWeight: 700,
            whiteSpace: "nowrap",
          }}
        >
          {c.t}
        </th>
      ))}
    </tr>
  </thead>
);

const td = (a?: "left" | "right" | "center"): CSSProperties => ({
  padding: "13px 20px",
  textAlign: a || "left",
  borderTop: "1px solid var(--bg-surface-2)",
});

// Row action buttons (edit + delete).
const RowActions = ({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) => (
  <span style={{ display: "inline-flex", gap: 4, justifyContent: "center" }}>
    <button
      type="button"
      onClick={onEdit}
      title="수정"
      style={{
        background: "transparent",
        border: 0,
        color: "var(--text-dim)",
        cursor: "pointer",
        display: "inline-flex",
      }}
    >
      <Icon name="settings" size={15} />
    </button>
    <button
      type="button"
      onClick={onDelete}
      title="삭제"
      style={{
        background: "transparent",
        border: 0,
        color: "var(--text-dim)",
        cursor: "pointer",
        display: "inline-flex",
      }}
    >
      <Icon name="trash" size={15} />
    </button>
  </span>
);

// 고객사 담당자 복수 행 편집기 — 이름/연락처 쌍을 추가·삭제한다.
const ContactsEditor = ({
  contacts,
  onChange,
}: {
  contacts: Contact[];
  onChange: (next: Contact[]) => void;
}) => {
  const setRow = (i: number, patch: Partial<Contact>) =>
    onChange(contacts.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const addRow = () => onChange([...contacts, { name: "", tel: "" }]);
  const delRow = (i: number) => onChange(contacts.filter((_, idx) => idx !== i));
  return (
    <Field label="담당자" en="Contacts" hint="여러 명 등록 가능">
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {contacts.map((c, i) => (
          // 행 구분용 안정적 key 가 없어 index 사용 — 추가/삭제만 있어 재정렬 없음.
          <div key={`contact-${i}`} style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ flex: 1.2 }}>
              <TextInput
                value={c.name}
                onChange={(v) => setRow(i, { name: v })}
                placeholder="담당자명 (예: 김도현 책임)"
              />
            </div>
            <div style={{ flex: 1 }}>
              <TextInput
                value={c.tel}
                onChange={(v) => setRow(i, { tel: v })}
                placeholder="연락처"
              />
            </div>
            <button
              type="button"
              onClick={() => delRow(i)}
              title="담당자 삭제"
              disabled={contacts.length === 1}
              style={{
                background: "transparent",
                border: 0,
                color: "var(--text-dim)",
                cursor: contacts.length === 1 ? "default" : "pointer",
                opacity: contacts.length === 1 ? 0.35 : 1,
                display: "inline-flex",
              }}
            >
              <Icon name="trash" size={15} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addRow}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            alignSelf: "flex-start",
            background: "transparent",
            border: 0,
            color: "var(--brand)",
            cursor: "pointer",
            fontFamily: "inherit",
            fontSize: 13,
            fontWeight: 600,
            padding: 0,
          }}
        >
          <Icon name="plus" size={14} /> 담당자 추가
        </button>
      </div>
    </Field>
  );
};

// Generic modal shell.
const Modal = ({
  title,
  onClose,
  children,
  onSave,
  saving,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  onSave: () => void;
  saving: boolean;
}) => (
  <div
    onClick={onClose}
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.6)",
      zIndex: 60,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: 440,
        maxWidth: "calc(100vw - 32px)",
        maxHeight: "90vh",
        overflowY: "auto",
        background: "var(--bg-surface)",
        borderRadius: 12,
        boxShadow: "rgba(0,0,0,0.5) 0px 8px 24px",
        padding: 24,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", marginBottom: 18 }}>
        <div style={{ fontFamily: "var(--font-title)", fontWeight: 800, fontSize: 17 }}>
          {title}
        </div>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={onClose}
          style={{
            background: "transparent",
            border: 0,
            color: "var(--text-dim)",
            cursor: "pointer",
            display: "flex",
          }}
        >
          <Icon name="x" size={18} />
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>{children}</div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 22 }}>
        <Pill variant="ghost" size="sm" onClick={onClose}>
          취소
        </Pill>
        <Pill variant="primary" size="sm" disabled={saving} onClick={onSave}>
          저장
        </Pill>
      </div>
    </div>
  </div>
);

type Draft = Record<string, string>;

// Field definition driving the 고객사 add/edit form.
interface FieldDef {
  key: string;
  label: string;
  en?: string;
  numeric?: boolean;
  select?: string[];
}

// 담당자(contacts)는 복수 행 편집기로 별도 처리 — 여기엔 단일 값 필드만 둔다.
const CUSTOMER_FIELDS: FieldDef[] = [
  { key: "name", label: "고객사명", en: "Name" },
  { key: "biz", label: "사업자번호", en: "Biz No." },
  { key: "ceo", label: "대표자", en: "CEO" },
];

const MASTER_HELP = [
  "견적의 기준이 되는 고객사 정보를 관리합니다 — 고객사명 · 사업자번호 · 대표자 · 담당자 · 연락처.",
  "'신규 등록' 버튼으로 새 고객사를 추가하고, 각 행의 아이콘으로 수정 · 삭제합니다.",
  "담당자는 고객사별로 여러 명 등록할 수 있습니다.",
  "고객사 정보를 변경해도 기존 견적에는 영향이 없습니다 — 견적별 스냅샷으로 보존됩니다.",
];

export const MasterScreen = ({ onToast }: { onToast: (msg: string) => void }) => {
  const mobile = useIsMobile();
  const customersRes = useCustomers();

  // Modal state: which record (or null for "add new"), and the editable draft.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<Draft>({});
  // 고객사 담당자 복수 행 — Draft(문자열 맵)로 표현 불가해 별도 상태로 둔다.
  const [contactsDraft, setContactsDraft] = useState<Contact[]>([]);
  const [saving, setSaving] = useState(false);

  const fields = CUSTOMER_FIELDS;
  const res = customersRes;

  const openAdd = () => {
    setDraft({});
    setContactsDraft([{ name: "", tel: "" }]);
    setEditingId(null);
    setAdding(true);
  };
  const openEdit = (rec: Customer) => {
    const r = rec as unknown as Record<string, unknown>;
    const d: Draft = {};
    for (const f of fields) d[f.key] = String(r[f.key] ?? "");
    setDraft(d);
    const existing = rec.contacts ?? [];
    setContactsDraft(existing.length ? existing.map((c) => ({ ...c })) : [{ name: "", tel: "" }]);
    setEditingId(rec.id);
    setAdding(false);
  };
  const closeModal = () => {
    setAdding(false);
    setEditingId(null);
    setDraft({});
    setContactsDraft([]);
  };

  const handleSave = async () => {
    // Build a typed patch — numeric fields parsed with Number.parseFloat.
    const patch: Record<string, unknown> = {};
    for (const f of fields) {
      const raw = draft[f.key] ?? "";
      patch[f.key] = f.numeric ? Number.parseFloat(raw) || 0 : raw;
    }
    // 완전히 빈 행은 제외하고, 이름/연락처 공백은 다듬어 저장.
    patch.contacts = contactsDraft
      .map((c) => ({ name: c.name.trim(), tel: c.tel.trim() }))
      .filter((c) => c.name || c.tel);
    setSaving(true);
    try {
      if (editingId) {
        await res.update(editingId, patch as Partial<Customer>);
        onToast("수정되었습니다");
      } else {
        // 백엔드 Create 모델은 비즈니스 id 필수 — 클라이언트에서 생성 (CncMasterScreen 과 동일 패턴).
        patch.id = `c-${Date.now().toString(36)}`;
        await res.create(patch as Omit<Customer, "id"> & { id?: string });
        onToast("등록되었습니다");
      }
      closeModal();
    } catch (e) {
      onToast(e instanceof Error ? e.message : "저장에 실패했습니다");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, label: string) => {
    if (!window.confirm(`'${label}' 항목을 삭제하시겠습니까?`)) return;
    try {
      await res.remove(id);
      onToast("삭제되었습니다");
    } catch (e) {
      onToast(e instanceof Error ? e.message : "삭제에 실패했습니다");
    }
  };

  const modalOpen = adding || editingId !== null;
  const modalTitle = `${editingId ? "수정 — " : "신규 등록 — "}고객사`;

  return (
    <div
      style={{
        padding: mobile ? "20px 14px 56px" : "24px 28px 56px",
        display: "flex",
        flexDirection: "column",
        gap: 18,
      }}
    >
      <SectionTitle
        sub="원가 자동 계산의 기준이 되는 마스터 데이터"
        right={
          <Pill variant="primary" size="sm" icon={<Icon name="plus" size={16} />} onClick={openAdd}>
            신규 등록
          </Pill>
        }
      >
        고객사 정보 관리
      </SectionTitle>

      <ScreenHelp items={MASTER_HELP} />

      <Card pad={0} style={{ overflow: "hidden" }}>
        {res.loading ? (
          <LoadingState />
        ) : res.error ? (
          <ErrorState error={res.error} onRetry={() => void res.reload()} />
        ) : (
          <>
            <TableScroll>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
                <Head
                  cols={[
                    { t: "고객사명" },
                    { t: "사업자번호" },
                    { t: "대표자" },
                    { t: "담당자" },
                    { t: "연락처" },
                    { t: "", a: "center" },
                  ]}
                />
                <tbody>
                  {customersRes.data.map((c) => (
                    <tr key={c.id}>
                      <td style={{ ...td(), fontWeight: 700 }}>{c.name}</td>
                      <td style={{ ...td(), fontFamily: "monospace", color: "var(--text-near)" }}>
                        {c.biz}
                      </td>
                      <td style={{ ...td(), color: "var(--text-near)" }}>{c.ceo}</td>
                      <td style={{ ...td(), color: "var(--text-near)" }}>
                        {c.contacts.length ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {c.contacts.map((ct, i) => (
                              <span key={`${c.id}-name-${i}`}>{ct.name}</span>
                            ))}
                          </div>
                        ) : (
                          <span style={{ color: "var(--text-faint)" }}>—</span>
                        )}
                      </td>
                      <td style={{ ...td(), fontFamily: "monospace", color: "var(--text-dim)" }}>
                        {c.contacts.length ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {c.contacts.map((ct, i) => (
                              <span key={`${c.id}-tel-${i}`}>{ct.tel}</span>
                            ))}
                          </div>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td style={td("center")}>
                        <RowActions
                          onEdit={() => openEdit(c)}
                          onDelete={() => void handleDelete(c.id, c.name)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
            <div
              style={{
                padding: "12px 20px",
                borderTop: "1px solid var(--bg-surface-2)",
                fontSize: 12.5,
                color: "var(--text-faint)",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Icon name="layers" size={14} color="var(--brand)" />
              고객사 정보를 변경해도 기존 견적에 영향을 주지 않습니다 — 견적별 스냅샷으로
              보존됩니다.
            </div>
          </>
        )}
      </Card>

      {modalOpen && (
        <Modal
          title={modalTitle}
          onClose={closeModal}
          onSave={() => void handleSave()}
          saving={saving}
        >
          {fields.map((f) => (
            <Field key={f.key} label={f.label} en={f.en}>
              {f.select ? (
                <Select
                  value={draft[f.key] ?? ""}
                  onChange={(v) => setDraft((d) => ({ ...d, [f.key]: v }))}
                  options={f.select}
                  placeholder="선택"
                />
              ) : (
                <TextInput
                  value={draft[f.key] ?? ""}
                  onChange={(v) => setDraft((d) => ({ ...d, [f.key]: v }))}
                  type={f.numeric ? "number" : "text"}
                  mono={f.numeric}
                />
              )}
            </Field>
          ))}
          <ContactsEditor contacts={contactsDraft} onChange={setContactsDraft} />
        </Modal>
      )}
    </div>
  );
};
